import { FC, useCallback, useEffect, useRef, useState, useContext, useMemo } from "react";
import { ICasinoSessionResponse } from "@finbackoffice/clientbff-client";
import { useRouter } from "next/router";
import { Currency } from "@finbackoffice/enums";
import {
    AuthContext,
    CasinoScopeEnum,
    ClientBFFContext,
    LocaleContext,
    ModalsContext,
    useCasinoGames,
    UserAccountContext,
} from "@finbackoffice/site-core";
import { TranslationScopes } from "@finbackoffice/enums";
import Loading from "components/base/loading/Loading";
import ToggleSwitch from "components/base/toggle-switch/ToggleSwitch";
import Translate from "components/base/translate/Translate";
import { NotificationContext } from "contexts";
import { Svg } from "components/base/svg/Svg";
import { ModalTypes, RouteNames } from "utils/constants";
import styles from "./casino-game.module.sass";

interface IProps {
    scope: CasinoScopeEnum;
}

type ISessionState = {
    session: ICasinoSessionResponse | null;
    loading: boolean;
};

const CasinoGame: FC<IProps> = ({ scope }) => {
    const router = useRouter();
    const { notice } = useContext(NotificationContext);
    const { loginModalRef } = useContext(ModalsContext);
    const client = useContext(ClientBFFContext);
    const { locale } = useContext(LocaleContext);
    const { currentCurrency, currentWalletId } = useContext(UserAccountContext);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { currentModal } = useContext(ModalsContext);
    const [sessionState, setSessionState] = useState<ISessionState>({
        session: null,
        loading: false,
    });
    const iframeRef = useRef(null);
    const isMounted = useRef(false);
    const isPlayForFun = router.query.forFun === "true";
    const gameTypesMemo = useMemo(() => [], []);
    const [showFallbackBackground, setShowFallbackBackground] = useState(false);
    const { casinoGamesState } = useCasinoGames(gameTypesMemo, "", "All", {
        gameId: showFallbackBackground ? (router.query?.gameId as string) : undefined,
    });

    const navigateToCasinoGamesList = useCallback(() => {
        router.push({
            pathname: `/${RouteNames.CASINO}/${scope}`,
        });
    }, [router, scope]);

    const handleFullScreen = () => {
        if (iframeRef.current) {
            const iframe: HTMLIFrameElement = iframeRef.current;
            if (
                iframe &&
                (document.fullscreenEnabled ||
                    (document as any).webkitFullscreenEnabled ||
                    (document as any).mozFullScreenEnabled ||
                    (document as any).msFullscreenEnabled)
            ) {
                if (iframe.requestFullscreen) {
                    iframe.requestFullscreen();
                } else if ((iframe as any).webkitRequestFullscreen) {
                    (iframe as any).webkitRequestFullscreen();
                } else if ((iframe as any).mozRequestFullScreen) {
                    (iframe as any).mozRequestFullScreen();
                } else if ((iframe as any).msRequestFullscreen) {
                    (iframe as any).msRequestFullscreen();
                }
            } else {
                console.log("Your browser is not supported");
            }
        }
    };

    const changeHandler = useCallback(() => {
        const isForFun = router.query.forFun === "true";
        if (isUserLoggedIn) {
            router.push({
                pathname: router.pathname,
                query: {
                    ...router.query,
                    forFun: !isForFun,
                },
            });
        } else {
            loginModalRef.current?.open();
        }
    }, [router, isUserLoggedIn, loginModalRef]);

    const loadData = useCallback(async () => {
        setSessionState((state) => ({
            ...state,
            loading: true,
        }));

        const isPlayForFun = router.query.forFun === "true";

        const request = {
            game_id: router.query?.gameId as string,
            homeurl:
                NODE_ENV === "production"
                    ? `${window.location.origin}/${RouteNames.CASINO}/${scope}`
                    : `https://m.${window.location.hostname}/${RouteNames.CASINO}/${scope}`,
            language: locale,
        };

        try {
            let response: ICasinoSessionResponse | null = null;
            const session = async () => {
                if (isPlayForFun) {
                    return await client.createCasinoFunSession({
                        ...request,
                        currency: Currency.EUR,
                    });
                } else if (currentWalletId) {
                    return await client.createCasinoRealSession({
                        ...request,
                        wallet_id: currentWalletId,
                    });
                }
                return null;
            };

            response = await session();

            if (isMounted) {
                if (!response?.iframe_link) {
                    notice({
                        type: "error",
                        title: "Error",
                        message: "No iframe link",
                    });
                }

                setSessionState((state) => ({
                    ...state,
                    session: response,
                    loading: false,
                }));
            }
        } catch (error) {
            if (isMounted) {
                setSessionState((state) => ({
                    ...state,
                    loading: false,
                }));
            }
        }
    }, [client, currentWalletId, locale, notice, router.query.forFun, router.query?.gameId, scope]);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isPlayForFun && !isUserLoggedIn) {
            if (currentModal !== ModalTypes.LOGIN) {
                if (sessionState.session?.iframe_link) {
                    setSessionState((state) => ({
                        ...state,
                        session: null,
                    }));
                }
                setShowFallbackBackground(true);
                loginModalRef.current?.open();
            }
        } else if (currentCurrency) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCurrency, router.query.forFun, currentWalletId]);

    const windowMessageHandler = useCallback(
        (e: any) => {
            if (e.data.name === "gameQuit" || e.data.type === "Window.Redirect") {
                navigateToCasinoGamesList();
            }
        },
        [navigateToCasinoGamesList],
    );

    useEffect(() => {
        window.addEventListener("message", windowMessageHandler);

        return () => {
            window.removeEventListener("message", windowMessageHandler);
        };
    }, [windowMessageHandler]);

    return (
        <div
            className={styles.casinoGame}
            style={
                casinoGamesState.games[0]?.image_background
                    ? {
                          backgroundImage: `url(${casinoGamesState.games[0]?.image_background})`,
                          backgroundSize: "cover",
                          opacity: 0.5,
                          boxShadow: "none",
                      }
                    : undefined
            }>
            {sessionState.loading && <Loading testId="casino-game-loader" />}
            {sessionState.session?.iframe_link && (
                <>
                    <div className={styles.casinoGameHeader}>
                        <div className={styles.casinoGameHeaderLeft}>
                            {sessionState.session.game_name}
                        </div>
                        {sessionState.session.play_for_fun && (
                            <div className="ui-switch-container">
                                <Translate
                                    tid="casino_for_fun"
                                    namespace={TranslationScopes.Casino}
                                />
                                <ToggleSwitch
                                    name="fun-real"
                                    checked={router.query.forFun === "false"}
                                    onChange={changeHandler}
                                />
                                <Translate
                                    tid="casino_real_play"
                                    namespace={TranslationScopes.Casino}
                                />
                            </div>
                        )}
                        <div className={styles.tools}>
                            <Svg
                                src="/common/desktop/base-icons/casino-favorite.svg"
                                wrapper="span"
                                className={styles.icon}
                            />
                            <a
                                title="New window"
                                href={sessionState.session?.iframe_link}
                                target="_blank"
                                rel="noreferrer">
                                <Svg
                                    src="/common/desktop/base-icons/casino-game-new-window.svg"
                                    wrapper="span"
                                    className="svg-icon"
                                />
                            </a>
                            <span
                                className={styles.icon}
                                title="Fullscreen"
                                onClick={handleFullScreen}>
                                <Svg
                                    src="/common/desktop/base-icons/casino-game-fullscreen.svg"
                                    wrapper="span"
                                    className="svg-icon"
                                />
                            </span>
                            <Svg
                                src="/common/desktop/base-icons/casino-game-reload.svg"
                                wrapper="span"
                                className={styles.icon}
                            />
                            <Svg
                                src="/common/desktop/base-icons/casino-game-close.svg"
                                wrapper="span"
                                className={styles.icon}
                                onClick={() =>
                                    router.push({
                                        pathname: `/${RouteNames.CASINO}/${scope}`,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.iframeContainer} data-testid="casino-iframe-container">
                        <iframe
                            title={sessionState.session?.iframe_link}
                            width="100%"
                            height="100%"
                            ref={iframeRef}
                            // onLoad={onLoad}
                            id={router.query?.gameId as string}
                            // height={virtualModel.iframeHeight}
                            // name={`${virtualModel.name}_iframe`}
                            src={sessionState.session?.iframe_link}
                            frameBorder="0"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CasinoGame;
